import type { FlatResponseData } from '@sa/axios';

type RecordValue = Record<string, unknown>;

function isRecord(value: unknown): value is RecordValue {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toPositiveInt(value: unknown, fallback: number) {
  const num = Number(value);

  if (Number.isInteger(num) && num > 0) {
    return num;
  }

  return fallback;
}

function toNonNegativeInt(value: unknown, fallback: number) {
  const num = Number(value);

  if (Number.isInteger(num) && num >= 0) {
    return num;
  }

  return fallback;
}

function resolvePage(params?: Api.Common.CommonSearchParams) {
  return toPositiveInt(params?.page ?? params?.current, 1);
}

function resolvePerPage(params?: Api.Common.CommonSearchParams, fallback = 10) {
  return toPositiveInt(params?.per_page ?? params?.page_size ?? params?.size ?? params?.limit, fallback);
}

export function normalizeBusinessResponseEnvelope<T>(payload: T): T {
  if (!isRecord(payload) || 'msg' in payload || typeof payload.message !== 'string') {
    return payload;
  }

  return {
    ...payload,
    msg: payload.message
  } as T;
}

export function normalizeListParams<T extends object | undefined>(params?: T) {
  if (!params) {
    return params;
  }

  const normalized: Record<string, unknown> = { ...params };
  const page = normalized.page ?? normalized.current;
  const perPage = normalized.per_page ?? normalized.page_size ?? normalized.size ?? normalized.limit;

  if (page !== undefined && page !== null) {
    normalized.page = page;
  }

  if (perPage !== undefined && perPage !== null) {
    normalized.per_page = perPage;
  }

  delete normalized.current;
  delete normalized.size;
  delete normalized.limit;
  delete normalized.page_size;

  return normalized as T;
}

export function normalizeListData<T>(payload: unknown, params?: Api.Common.CommonSearchParams): Api.Common.PageData<T> {
  const page = resolvePage(params);
  const fallbackPerPage = resolvePerPage(params);

  if (Array.isArray(payload)) {
    const total = payload.length;
    const perPage = resolvePerPage(params, Math.max(total, 1));
    const lastPage = total === 0 ? 1 : Math.max(1, Math.ceil(total / perPage));

    return {
      list: payload as T[],
      meta: {
        current_page: page,
        per_page: perPage,
        total,
        from: total === 0 ? 0 : 1,
        to: total,
        last_page: lastPage
      }
    };
  }

  const data = isRecord(payload) ? payload : {};
  const list = Array.isArray(data.list) ? (data.list as T[]) : Array.isArray(data.records) ? (data.records as T[]) : [];
  const metaSource = isRecord(data.meta) ? data.meta : data;

  const currentPage = toPositiveInt(metaSource.current_page ?? metaSource.current, page);
  const perPage = toPositiveInt(
    metaSource.per_page ?? metaSource.page_size ?? metaSource.size ?? metaSource.limit,
    fallbackPerPage
  );
  const total = toNonNegativeInt(metaSource.total, list.length);
  const fromDefault = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toDefault = total === 0 ? 0 : Math.min(total, fromDefault + list.length - 1);
  const lastPageDefault = total === 0 ? 1 : Math.max(1, Math.ceil(total / perPage));

  return {
    list,
    meta: {
      current_page: currentPage,
      per_page: perPage,
      total,
      from: toNonNegativeInt(metaSource.from, fromDefault),
      to: toNonNegativeInt(metaSource.to, toDefault),
      last_page: toPositiveInt(metaSource.last_page, lastPageDefault)
    }
  };
}

export function normalizeListResponse<T, ResponseData = any>(
  response: FlatResponseData<any, ResponseData>,
  params?: Api.Common.CommonSearchParams
): FlatResponseData<Api.Common.PageResult<T>, ResponseData> {
  if (response.error || !response.data) {
    return response as FlatResponseData<Api.Common.PageResult<T>, ResponseData>;
  }

  const rawEnvelope = normalizeBusinessResponseEnvelope(response.data);
  const envelope = isRecord(rawEnvelope)
    ? rawEnvelope
    : ({
        code: 0,
        msg: 'success',
        data: rawEnvelope
      } satisfies RecordValue);

  return {
    ...response,
    data: {
      ...envelope,
      code: toNonNegativeInt(envelope.code, 0),
      msg: typeof envelope.msg === 'string' ? envelope.msg : 'success',
      data: normalizeListData<T>(envelope.data, params)
    } as Api.Common.PageResult<T>
  };
}
